import React, { useState } from 'react';
import { IconButton, Menu, MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DocumentEditButtonProps } from './types';
import { useDocumentEditButton } from './useDocumentEditButton';
import VerticalDotsIcon from '../../../assets/svg/more_vert.svg?react';
import { useDocumentDuplicating } from '../../pages/document-vault/hooks/useDocumentDuplicating';
import MessageModal from '../message-modal/MessageModal';
import {
  addDocumentContract,
  fetchDocumentContracts,
} from '../../slices/document-contracts/documentContractsSlice';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { toast } from 'react-toastify';
import { useAppSelector } from '../hooks/useAppSelector';
import { selectUser } from '../../slices/user/userSlice';
import { NWClient } from '../../client/NWClient';
import { showMessage } from '../../slices/show-plan-upgrading-message/showPlanUpgradingMessage';
import BookACallModal from '../book-a-call-modal/BookACallModal';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import { useErrorMessage } from '../hooks/useErrorMessage';

type DocumentDupeData = {
  category?: string;
  employees: number[];
  suppliers: number[];
  customers: number[];
  business_profile_contacts: number[];
  user: number;
  extras?: {
    start_date?: string | null;
    end_date?: string | null;
    subscription?: boolean;
    amount?: number | null;
    currency?: string | null;
    frequency?: string | null;
    is_signed?: boolean;
  };
};

export const DocumentsEditButton = ({
  getDocumentSummaryFn,
  documentVersionsFn,
  deleteDocumentFn,
  documentLabelsFn,
  createTemplateFn,
  document,
  isImage,
}: DocumentEditButtonProps) => {
  const { t } = useTranslation();
  const { getErrorMessage } = useErrorMessage();
  const { anchorEl, setAnchorEl } = useDocumentEditButton();
  const open = Boolean(anchorEl);
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectUser);
  const [isDocReviewOpen, setIsDocReviewOpen] = useState(false);
  const company = useAppSelector(selectAllCompanies)[0];

  const {
    document: documentToDuplicate,
    setDocument: setDocumentToDuplicate,
    dialogOpen: duplicatingDialogOpen,
    setDialogOpen: setDuplicatingDialogOpen,
    handleDuplicateOriginalDocument,
    handleDuplicateLatestVersion,
  } = useDocumentDuplicating();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleDocumentSummary = () => {
    getDocumentSummaryFn?.(document);
    handleClose();
  };
  const handleDocumentCreateTemplate = () => {
    createTemplateFn?.(document);
    handleClose();
  };
  const handleDocumentDelete = () => {
    deleteDocumentFn?.(document);
    handleClose();
  };
  const handleDocumentVersions = () => {
    documentVersionsFn?.(document);
    handleClose();
  };
  const handleDocumentLabels = () => {
    documentLabelsFn?.(document);
    handleClose();
  };

  const handleDocumentReview = () => {
    setIsDocReviewOpen(true);
  };
  const handleDocumentDuplicating = () => {
    if ('contract_generation' in document) {
      // Extract the arrays that need to be updated separately
      const relatedArrays: DocumentDupeData = {
        category: document.category || '',
        user: currentUser.id,
        employees: document.employees || [],
        suppliers: document.suppliers || [],
        customers: document.customers || [],
        business_profile_contacts: document.business_profile_contacts || [],
      };
      const categoriesPayload = {
        dc_types: document.dc_types || [],
        dc_workflows: document.dc_workflows || [],
        dc_label: document.dc_label || [],
      };

      // Format extras as an object instead of an array to match backend expectations
      if (document.extras && Array.isArray(document.extras) && document.extras.length > 0) {
        // Take the first item from the array since backend expects a dictionary
        const extra = document.extras[0];
        relatedArrays.extras = {
          start_date: extra.start_date || null,
          end_date: extra.end_date || null,
          subscription: extra.subscription || false,
          amount: Number(extra.amount) || null,
          currency: extra.currency || null,
          frequency: extra.frequency || null,
          is_signed: extra.is_signed || false,
        };
      }

      // Create document copy without these arrays
      const keysExcluded = [
        'dc_types',
        'dc_workflows',
        'dc_label',
        'extras',
        'employees',
        'suppliers',
        'customers',
      ];
      const keysFiltered = Object.keys(document).filter((key) => !keysExcluded.includes(key));
      const valuesInherited = Object.fromEntries(
        keysFiltered.map((key) => [key, (document as Record<string, any>)[key]])
      );

      const documentCopy = {
        ...valuesInherited,
        name: document.name ? `${document.name} - ${t('labels.copy')}` : t('labels.copy'),
      };
      handleClose();
      dispatch(addDocumentContract({ data: documentCopy }))
        .unwrap()
        .then((res: any) => {
          // Update the newly created document with the related arrays
          const newDocumentId = res?.id;
          if (newDocumentId) {
            return NWClient.patch(`document-contract/${newDocumentId}/`, null, relatedArrays).then(
              () => {
                return NWClient.patch(
                  `tag-document-contract/${newDocumentId}`,
                  null,
                  categoriesPayload
                ).then(() => {
                  dispatch(fetchDocumentContracts({ data: { user: currentUser.id } }));
                  toast.success(t('messages.documentDuplicated'));
                });
              }
            );
          }
        })
        .catch((error) => {
          if (error.errors?.length > 0 && error.errors[0] === t('messages.exceededPlanLimits')) {
            dispatch(showMessage(t('messages.monthLimitReached')));
          } else {
            toast.error(getErrorMessage(error));
          }
        });
    } else {
      setDocumentToDuplicate(document);
      //setDuplicatingDialogOpen(true);
      if (document) {
        handleDuplicateOriginalDocument(document);
      }
      handleClose();
    }
  };

  return (
    <>
      <IconButton
        id={`documentsEditButtonVerticalDotsIconBtn_${document?.id}`}
        aria-label={t('labels.menuButton')}
        size='small'
        onClick={handleClick}
      >
        <VerticalDotsIcon
          id={`documentsEditButtonVerticalDotsIconBtnVerticalDotsIcon_${document?.id}`}
        />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {Boolean(getDocumentSummaryFn) && !isImage && (
          <MenuItem
            id={`documentsEditButtonSummaryMenuItem_${document?.id}`}
            onClick={() => {
              handleDocumentSummary();
            }}
          >
            <span id={`documentsEditButtonSummaryMenuItemText_${document?.id}`}>
              {' '}
              {t('buttons.summary')}
            </span>
          </MenuItem>
        )}

        {!isImage && !document?.is_flattened && !document?.signing_doc_id && (
          <MenuItem
            id={`documentsEditButtonSendDocumentForReviewMenuItem_${document?.id}`}
            onClick={() => {
              handleDocumentReview();
            }}
          >
            <span id={`documentsEditButtonSendDocumentForReviewMenuItemText_${document?.id}`}>
              {' '}
              {t('labels.sendDocumentForReview')}
            </span>
          </MenuItem>
        )}

        {!isImage && !document?.is_flattened && !document?.signing_doc_id && (
          <MenuItem
            id={`documentsEditButtonCopyDocumentMenuItem_${document?.id}`}
            onClick={() => {
              handleDocumentDuplicating();
            }}
          >
            <span id={`documentsEditButtonCopyDocumentMenuItemText_${document?.id}`}>
              {' '}
              {t('buttons.copyDocument')}
            </span>
          </MenuItem>
        )}

        {!document?.contract_generation &&
          !isImage &&
          !document?.is_flattened &&
          Boolean(createTemplateFn) && (
            <MenuItem
              id={`documentsEditButtonCreateTemplateMenuItem_${document?.id}`}
              onClick={() => {
                handleDocumentCreateTemplate();
              }}
            >
              <span id={`documentsEditButtonCreateTemplateMenuItemText_${document?.id}`}>
                {' '}
                {t('buttons.createTemplate')}
              </span>
            </MenuItem>
          )}
        {Boolean(documentVersionsFn) && !isImage && !document?.is_flattened && (
          <MenuItem
            id={`documentsEditButtonVersionsMenuItem_${document?.id}`}
            onClick={() => {
              handleDocumentVersions();
            }}
          >
            <span id={`documentsEditButtonVersionsMenuItemText_${document?.id}`}>
              {t('buttons.versions')}
            </span>
          </MenuItem>
        )}
        {Boolean(documentLabelsFn) && (
          <MenuItem
            id={`documentsEditButtonLabelsMenuItem_${document?.id}`}
            onClick={() => {
              handleDocumentLabels();
            }}
          >
            <span id={`documentsEditButtonLabelsMenuItemText_${document?.id}`}>
              {' '}
              {t('buttons.labels')}
            </span>
          </MenuItem>
        )}
        {Boolean(deleteDocumentFn) && (
          <MenuItem
            id={`documentsEditButtonDeleteMenuItem_${document?.id}`}
            onClick={() => {
              handleDocumentDelete();
            }}
          >
            <span id={`documentsEditButtonDeleteMenuItemText_${document?.id}`}>
              {' '}
              {t('buttons.delete')}
            </span>
          </MenuItem>
        )}
      </Menu>
      {duplicatingDialogOpen && (
        <MessageModal
          open={duplicatingDialogOpen}
          modalMessage={t('messages.duplicatingVersionMessage')}
          actionButtonText={t('buttons.duplicateLatestDocument')}
          actionButtonFn={() => {
            if (documentToDuplicate) {
              handleDuplicateLatestVersion(documentToDuplicate);
            }
          }}
          actionButtonAdditionalText={t('buttons.duplicateOriginalDocument')}
          actionButtonAdditionalFn={() => {
            if (documentToDuplicate) {
              handleDuplicateOriginalDocument(documentToDuplicate);
            }
          }}
          goBackButtonText={t('buttons.goBack')}
          onClose={() => {
            setDocumentToDuplicate(null);
            setDuplicatingDialogOpen(false);
          }}
        />
      )}

      {isDocReviewOpen &&
        ('contract_generation' in document ? (
          <BookACallModal
            company={company}
            user={currentUser}
            open={isDocReviewOpen}
            document_contract={document.id}
            onClose={() => setIsDocReviewOpen(false)}
          />
        ) : (
          <BookACallModal
            company={company}
            user={currentUser}
            open={isDocReviewOpen}
            document_upload={document.id}
            onClose={() => setIsDocReviewOpen(false)}
          />
        ))}
    </>
  );
};
